import React, { useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import retina from "retinajs";
import { AnimatePresence } from "framer-motion";
import { Toaster } from 'react-hot-toast';

// Context
import GlobalContext from "./Context/Context";
import { CartProvider } from "./contexts/CartContext";

// Components
import ScrollToTopButton from "./Components/ScrollToTop";

// Pages critiques (chargées immédiatement)
import HomePage from "./Pages/HomePage";
import ProductsPage from "./Pages/ProductsPage";
import ProductDetailPage from "./Pages/ProductDetailPage";
import ProductsCatalogPage from "./Pages/ProductsCatalogPage";
import RangePage from "./Pages/RangePage";
import AboutPage from "./Pages/AboutPage";
import ServicesPage from "./Pages/ServicesPage";
import ContactPage from "./Pages/ContactPage";

// Assets
import "./Assets/scss/icons.scss";
import "./Assets/scss/global.scss";

// Pages secondaires (lazy loading pour réduire bundle initial)
const NewsPage = lazy(() => import("./Pages/NewsPage"));
const NewsDetailPage = lazy(() => import("./Pages/NewsDetailPage"));
const CGVPage = lazy(() => import("./Pages/CGVPage"));
const MentionsLegalesPage = lazy(() => import("./Pages/MentionsLegalesPage"));
const AdminDashboard = lazy(() => import("./Pages/admin/AdminDashboard"));
const LoginPage = lazy(() => import("./Pages/auth/LoginPage"));

// Fallback loader
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#fff'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3B82F6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  const location = useLocation();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customModal, setCustomModal] = useState({
    el: null,
    isOpen: false
  });

  // RetinaJS
  useEffect(() => {
    window.addEventListener("load", retina(document.querySelectorAll("img")));
  }, []);

  return (
    <CartProvider>
      <GlobalContext.Provider
        value={{
          headerHeight,
          setHeaderHeight,
          footerHeight,
          setFooterHeight,
          isModalOpen,
          setIsModalOpen,
          customModal,
          setCustomModal
        }}
      >
        <div className="App" style={{ "--header-height": "74px" }}>
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#fff',
                color: '#1f2937',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                fontSize: '14px',
                fontWeight: '500',
              },
              success: {
                iconTheme: {
                  primary: '#3B82F6',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          {location.pathname !== "/login" && <ScrollToTopButton />}
          <main>
            <AnimatePresence mode="wait">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/produits" element={<ProductsCatalogPage />} />
                  <Route path="/produits/:categorie" element={<ProductsPage />} />
                  <Route path="/produit/:slug" element={<ProductDetailPage />} />
                  <Route path="/gamme/:slug" element={<RangePage />} />
                  <Route path="/a-propos" element={<AboutPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/actualites" element={<NewsPage />} />
                  <Route path="/actualites/:slug" element={<NewsDetailPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/cgv" element={<CGVPage />} />
                  <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                  {/* Auth & Admin */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="*" element={<HomePage />} />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </main>
        </div>
      </GlobalContext.Provider>
    </CartProvider>
  );
}

export default App;
